/**
 * Композабл для URL изображений с бэкенда с опциональным сжатием/ресайзом (как Nuxt Image).
 *
 * Если задан VITE_IMAGE_PROXY_URL, URL подменяется на прокси с параметрами w, q, format.
 * Иначе возвращается исходный URL.
 *
 * Прокси — собственный бэкенд-эндпоинт, например:
 *   https://api.chapterfour.ru/image?url=...&w=800&q=85&f=webp
 */

import { computed, toValue } from 'vue'
import type { ComputedRef, MaybeRefOrGetter } from 'vue'

export interface OptimizedImageOptions {
  width?: number
  height?: number
  quality?: number
  format?: string
}

const PROXY_BASE = import.meta.env.VITE_IMAGE_PROXY_URL || ''

/**
 * Строит URL оптимизированного изображения через прокси.
 */
export function getOptimizedImageUrl(rawUrl: string, options: OptimizedImageOptions = {}): string {
  if (!rawUrl || typeof rawUrl !== 'string') return ''
  const { width, height, quality = 85, format = 'webp' } = options
  if (!PROXY_BASE) return rawUrl.trim()
  if (rawUrl.startsWith(PROXY_BASE)) return rawUrl.trim()

  const absoluteUrl = rawUrl.startsWith('http')
    ? rawUrl
    : new URL(rawUrl, window.location.origin).href
  const params = new URLSearchParams()
  params.set('url', absoluteUrl)
  if (width) params.set('w', String(width))
  if (height) params.set('h', String(height))
  if (quality) params.set('q', String(quality))
  if (format) params.set('f', format)

  const sep = PROXY_BASE.includes('?') ? '&' : '?'
  // URLSearchParams кодирует пробелы как '+'. Заменяем на '%20' для максимальной
  // совместимости (оригинальные '+' в URL уже закодированы как '%2B').
  return `${PROXY_BASE}${sep}${params.toString().replace(/\+/g, '%20')}`
}

/**
 * Композабл: реактивный оптимизированный URL для одного изображения.
 */
export function useOptimizedImage(
  url: MaybeRefOrGetter<string | undefined | null>,
  options: MaybeRefOrGetter<OptimizedImageOptions> = {},
): ComputedRef<string> {
  return computed(() => {
    const u = toValue(url)
    const opts = toValue(options) || {}
    return getOptimizedImageUrl(u || '', opts)
  })
}
